/**
 * GroceryAddFlow — aisle tile → one scrolling aisle → the item sheet.
 *
 * Shared shape, grocery wording only. Full-screen on a phone with a Back button
 * that is ALWAYS there; a large panel from 768px up. Everything it draws comes
 * from the master catalog through the API — no product data lives here.
 *
 * Option chips are SKIPPABLE. A tap on an item the household has chosen before
 * adds it straight away with that choice; the first time, it asks "Which kind?"
 * with a clear "Skip, any is fine" that records "any" for the picker.
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ArrowLeft, Search, X, Heart, Minus, Plus, Loader2, Check, Pencil, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import GroceryIcon from './GroceryIcon';
import { QtyRow } from './GroceryQtyPanel';
import { ProductOptionsCard } from './ProductOptionsCard';
import { SheetLayer } from '../../lib/phoneSheet';

const PO_EMPTY = { form: '', type: '', brand: '', note: '' };

const API = (typeof window !== 'undefined' ? window.location.origin : '');

const api = async (path, { method = 'GET', body, session, lang } = {}) => {
    const sep = path.includes('?') ? '&' : '?';
    const r = await fetch(`${API}/api/grocery${path}${sep}lang=${lang || 'en'}`, {
        method,
        headers: { 'Content-Type': 'application/json', 'X-Grocery-Session': session },
        body: body ? JSON.stringify(body) : undefined,
    });
    if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || `http_${r.status}`);
    return r.json();
};

/** One-tap add for a favourite: it already knows the exact choice, so the only
 *  question left is how many. */
export const FavouriteRoller = ({ favourite, session, lang, memberId, onAdded, onClose,
    frost = '' }) => {
    const { t } = useTranslation();
    const [qty, setQty] = useState(Math.max(1, Number(favourite.last_qty) || 1));
    const [busy, setBusy] = useState(false);
    const packages = favourite.sold_by === 'weight' || favourite.sold_by === 'either';
    const confirm = async (n = qty) => {
        setBusy(true);
        try {
            const r = await api('/items', { method: 'POST', session, lang,
                body: { catalog_id: favourite.catalog_id, options: favourite.options || [],
                    qty: n, member_id: memberId } });
            onAdded(r);
            onClose();
        } catch (e) {
            toast.error(e.message === 'member_cannot_add' ? t('grocery.cannotAdd') : t('grocery.addFailed'));
            setBusy(false);
        }
    };
    return (
        <Shell title={favourite.name} onClose={onClose} testId="grocery-favourite-sheet" frost={frost}>
            <div className="flex items-center gap-3">
                <GroceryIcon icon={favourite.icon} badge={favourite.badge} size={52} alt="" />
                <div>
                    <p className="text-base font-bold text-foreground">{favourite.name}</p>
                    <p className="text-sm text-muted-foreground" data-testid="grocery-favourite-options">
                        {(favourite.options || []).length
                            ? favourite.options.join(' · ')
                            : t('grocery.anyIsFine')}
                        {packages ? ` · ${t('grocery.byWeight')}` : ''}
                    </p>
                </div>
            </div>
            <QtyRow value={qty} max={packages ? 10 : 99} packages={packages} busy={busy}
                onChange={setQty} onAdd={(n) => confirm(n)} testId="grocery-favourite-qty" />
        </Shell>
    );
};

/** Full-screen on a phone, a large panel from 768px up. */
const Shell = ({ title, onBack, onClose, children, testId, frost = '' }) => {
    const { t } = useTranslation();
    return (
        <SheetLayer>
        <div className="yot-sheet z-[60] flex flex-col bg-white dark:bg-slate-950 md:items-center md:justify-center md:bg-slate-900/60 md:p-6" data-grocery-sheet="true"
            data-testid={testId}>
            <div className={`flex min-h-0 w-full flex-1 flex-col md:h-auto md:max-h-[86vh] md:max-w-2xl md:flex-none md:rounded-3xl md:bg-white md:shadow-2xl md:dark:bg-slate-950${frost}`}>
                <header className="flex shrink-0 items-center gap-2 border-b border-slate-200 px-3 py-3 dark:border-slate-800">
                    <button onClick={onBack || onClose} data-testid="grocery-add-back"
                        className="flex min-h-[44px] min-w-[44px] items-center gap-1.5 rounded-full px-3 text-sm font-semibold text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-800">
                        <ArrowLeft className="h-5 w-5" />{t('grocery.back')}
                    </button>
                    <p className="min-w-0 flex-1 truncate text-center text-base font-bold text-foreground">{title}</p>
                    <button onClick={onClose} data-testid="grocery-add-close"
                        aria-label={t('grocery.close')}
                        className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-full text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800">
                        <X className="h-5 w-5" />
                    </button>
                </header>
                <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-4 pb-44 pt-4 yot-sheet-scroll">{children}</div>
            </div>
        </div>
        </SheetLayer>
    );
};

/** THE DEAD-END KILLER — one row, their own words, kept as typed.
 *  Never translated, never sent to the chef, 60 characters. */
const TypeItRow = ({ label, placeholder, value, onChange, onSubmit, busy, testId, maxLength = 60 }) => {
    const { t } = useTranslation();
    return (
        <div className="space-y-2 rounded-2xl border border-dashed border-emerald-400/70 p-3"
            data-testid={testId}>
            <p className="text-sm font-bold text-foreground">{label}</p>
            <div className="flex gap-2">
                <Input value={value} maxLength={maxLength} placeholder={placeholder}
                    onChange={(e) => onChange(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter' && value.trim()) onSubmit(value.trim()); }}
                    data-testid={`${testId}-input`} className="min-h-[52px] flex-1 text-base" />
                <Button onClick={() => value.trim() && onSubmit(value.trim())}
                    disabled={busy || !value.trim()} data-launcher-avoid="lift"
                    data-testid={`${testId}-add`}
                    className="min-h-[52px] bg-emerald-600 px-5 text-base font-bold text-white hover:bg-emerald-700">
                    {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : t('grocery.addBtn')}
                </Button>
            </div>
        </div>
    );
};

const customMessage = (t, code) => ({
    custom_too_long: t('grocery.customTooLong'),
    custom_looks_like_contact: t('grocery.customNoContact'),
    custom_list_full: t('grocery.customListFull'),
    custom_empty: t('grocery.customEmpty'),
}[code] || t('grocery.addFailed'));

export default function GroceryAddFlow({ session, lang, memberId, onAdded, onClose,
    practice = false, frost = '' }) {
    const { t } = useTranslation();
    const [aisles, setAisles] = useState(null);
    const [aisle, setAisle] = useState(null);
    const [sheet, setSheet] = useState(null);       // the item sheet
    const [step, setStep] = useState(0);
    const [picked, setPicked] = useState([]);
    const [qty, setQty] = useState(1);
    const [q, setQ] = useState('');
    const [results, setResults] = useState([]);
    const [busy, setBusy] = useState(false);
    const [tapped, setTapped] = useState(null);
    // NO DEAD ENDS: typed text, the one aisle question, and the typed option.
    const [typed, setTyped] = useState('');
    const [aisleFor, setAisleFor] = useState(null);   // text waiting for its aisle
    const [optionText, setOptionText] = useState('');
    const [myEdit, setMyEdit] = useState(null);       // { id, text } inline rename
    const [po, setPo] = useState(null);               // kind/brand chips for the sheet
    const [poVal, setPoVal] = useState(PO_EMPTY);

    useEffect(() => {
        api('/aisles', { session, lang }).then((r) => setAisles(r.aisles)).catch(() => setAisles([]));
    }, [session, lang]);

    const search = async (value) => {
        setQ(value);
        if (value.trim().length < 2) { setResults([]); return; }
        try {
            const r = await api(`/catalog/search?q=${encodeURIComponent(value.trim())}`, { session, lang });
            setResults(r.results || []);
        } catch (_e) { setResults([]); }
    };

    const openAisle = async (id) => {
        setBusy(true);
        try { setAisle(await api(`/aisles/${id}`, { session, lang })); }
        finally { setBusy(false); }
    };

    /** Their own words, saved for this household and put on the list. */
    const saveCustom = async (text, category, quantity = 1, options = []) => {
        setBusy(true);
        try {
            const r = await api('/custom-items', { method: 'POST', session, lang,
                body: { text, category: category || null, qty: quantity, member_id: memberId,
                    options, add_to_list: true } });
            toast.success(t('grocery.savedForNextTime', { name: r.saved?.name || text }));
            setTyped(''); setAisleFor(null); setQ(''); setResults([]);
            onAdded(r.item || {});
            if (aisle) setAisle(await api(`/aisles/${aisle.aisle.id}`, { session, lang }));
            return true;
        } catch (e) {
            toast.error(customMessage(t, e.message));
            return false;
        } finally { setBusy(false); }
    };

    /** At most ONE question: the aisle, with a "Don't know" that files it under Other. */
    const askForAisle = (text) => setAisleFor(text);

    const renameSaved = async (id, text) => {
        try {
            await api(`/custom-items/${id}`, { method: 'PATCH', session, lang, body: { text } });
            setMyEdit(null);
            if (aisle) setAisle(await api(`/aisles/${aisle.aisle.id}`, { session, lang }));
        } catch (e) { toast.error(customMessage(t, e.message)); }
    };

    const deleteSaved = async (id) => {
        try {
            await api(`/custom-items/${id}`, { method: 'DELETE', session, lang });
            if (aisle) setAisle(await api(`/aisles/${aisle.aisle.id}`, { session, lang }));
        } catch (_e) { toast.error(t('grocery.addFailed')); }
    };

    const addNow = useCallback(async (catalogId, options, quantity = 1,
        productOptions = undefined) => {
        setBusy(true);
        setTapped(catalogId);
        try {
            const body = { catalog_id: catalogId, qty: quantity, member_id: memberId,
                shared: !memberId, practice };
            if (options !== undefined) body.options = options;
            if (productOptions) body.product_options = productOptions;
            const r = await api('/items', { method: 'POST', session, lang, body });
            onAdded(r);
            return true;
        } catch (e) {
            toast.error(e.message === 'member_cannot_add' ? t('grocery.cannotAdd') : t('grocery.addFailed'));
            return false;
        } finally {
            setBusy(false);
            setTimeout(() => setTapped(null), 400);
        }
    }, [session, lang, memberId, onAdded, practice, t]);

    /** A tap on an item: straight in if the household already chose, otherwise ask. */
    const tapItem = async (catalogId) => {
        setBusy(true);
        try {
            const info = await api(`/items/${catalogId}/options`, { session, lang });
            const settled = info.has_last_choice || (info.groups || []).length === 0;
            if (settled && info.ask_quantity === false) {
                await addNow(catalogId, undefined, 1);     // the old one-tap add
                return;
            }
            // WHAT KIND, WHICH BRAND. An item the shop's option file covers
            // opens straight on ONE card: the chips, the note and the number.
            let card = null;
            try {
                const r = await api(`/options/${catalogId}`, { session, lang });
                card = r && r.group ? r : null;
            } catch (_e) { card = null; }
            setPo(card);
            setPoVal(card && card.preselect ? { ...PO_EMPTY, ...card.preselect } : PO_EMPTY);
            if (card) {
                setSheet(info); setPicked([]);
                setStep((info.groups || []).length);
                setQty(Math.max(1, Number(info.last_qty) || 1));
                return;
            }
            // THE QUANTITY STEP REPLACES THE INSTANT ADD: a food whose choice is
            // already settled opens straight on "how many?", not on the options.
            setSheet(info); setPicked([]);
            setStep(settled ? (info.groups || []).length : 0);
            setQty(Math.max(1, Number(info.last_qty) || 1));
        } finally { setBusy(false); }
    };

    /** A tap on a food they typed themselves: the SAME sheet as a catalogue
     *  food — their own kinds, their remembered quantity, and a heart. */
    const tapSaved = async (savedId) => {
        setBusy(true);
        try {
            const info = await api(`/saved-items/${savedId}/options`, { session, lang });
            setSheet(info); setPicked([]);
            setStep((info.groups || []).length ? 0 : 0);
            setQty(Math.max(1, Number(info.last_qty) || 1));
        } catch (_e) {
            toast.error(t('grocery.addFailed'));
        } finally { setBusy(false); }
    };

    const toggleFavourite = async () => {
        const next = !sheet.is_favourite;
        try {
            if (sheet.saved_id) {
                if (next) {
                    await api('/favourites/custom', { method: 'POST', session, lang,
                        body: { saved_id: sheet.saved_id, options: picked.filter(Boolean) } });
                    toast.success(t('grocery.favouriteAdded', { name: sheet.name }));
                } else {
                    await api(`/favourites/custom/${sheet.saved_id}`,
                        { method: 'DELETE', session, lang });
                    toast(t('grocery.favouriteRemoved', { name: sheet.name }));
                }
            } else if (next) {
                await api('/favourites', { method: 'POST', session, lang,
                    body: { catalog_id: sheet.catalog_id, options: picked, member_id: memberId } });
                toast.success(t('grocery.favouriteAdded', { name: sheet.name }));
            } else {
                await api(`/favourites/${sheet.catalog_id}`, { method: 'DELETE', session, lang });
                toast(t('grocery.favouriteRemoved', { name: sheet.name }));
            }
            setSheet({ ...sheet, is_favourite: next });
        } catch (_e) { toast.error(t('grocery.addFailed')); }
    };

    // ── ONE question: which aisle? Large tiles, and a "Don't know" default ──
    if (aisleFor !== null) {
        return (
            <Shell title={t('grocery.whichAisle')} onBack={() => setAisleFor(null)} onClose={onClose} frost={frost}
                testId="grocery-custom-aisle">
                <p className="text-base font-bold text-foreground" data-testid="grocery-custom-aisle-text">
                    “{aisleFor}”
                </p>
                <p className="text-sm text-muted-foreground">{t('grocery.whichAisleNote')}</p>
                <button onClick={() => saveCustom(aisleFor, 'other')} disabled={busy}
                    data-testid="grocery-custom-aisle-unknown" data-launcher-avoid="lift"
                    className="min-h-[56px] w-full rounded-2xl bg-slate-100 px-4 text-base font-bold text-slate-700 dark:bg-slate-800 dark:text-slate-200">
                    {t('grocery.dontKnow')}
                </button>
                <div className="grid grid-cols-3 gap-3 sm:grid-cols-4">
                    {(aisles || []).map((a) => (
                        <button key={a.id} onClick={() => saveCustom(aisleFor, a.id)} disabled={busy}
                            data-testid={`grocery-custom-aisle-${a.id}`}
                            className="flex min-h-[96px] flex-col items-center justify-center gap-1.5 rounded-2xl border border-slate-200 p-2 text-center transition-colors hover:border-emerald-400 dark:border-slate-800">
                            <GroceryIcon icon={a.icon} size={38} alt="" />
                            <span className="text-xs font-semibold leading-tight text-foreground">{a.name}</span>
                        </button>
                    ))}
                </div>
            </Shell>
        );
    }

    // ── the item sheet: one question at a time, always skippable ──────────
    if (sheet) {
        const groups = sheet.groups || [];
        const group = groups[step];
        const done = step >= groups.length;
        const choose = (value) => {
            const next = [...picked];
            next[step] = value;
            setPicked(next);
            setStep(step + 1);
        };
        return (
            <Shell title={sheet.name} onBack={() => setSheet(null)} onClose={onClose} frost={frost}
                testId="grocery-item-sheet">
                <div className="flex items-center gap-3">
                    <GroceryIcon icon={sheet.icon} badge={sheet.badge} size={56} alt="" />
                    <div className="min-w-0 flex-1">
                        <p className="text-lg font-bold text-foreground">{sheet.name}</p>
                        <p className="text-sm text-muted-foreground">
                            {picked.filter(Boolean).join(' · ') || t('grocery.anyIsFine')}
                            {sheet.counts_packages ? ` · ${t('grocery.byWeight')}` : ''}
                        </p>
                    </div>
                    <button onClick={toggleFavourite} data-testid="grocery-item-favourite-heart"
                        aria-pressed={!!sheet.is_favourite}
                        aria-label={sheet.is_favourite ? t('grocery.removeFavourite') : t('grocery.addFavourite')}
                        className="flex min-h-[48px] min-w-[48px] items-center justify-center rounded-full border border-slate-200 dark:border-slate-700">
                        <Heart className={`h-6 w-6 ${sheet.is_favourite ? 'fill-rose-500 text-rose-500' : 'text-slate-400'}`} />
                    </button>
                </div>

                {!done ? (
                    <div className="space-y-3" data-testid={`grocery-option-group-${group.id}`}>
                        <p className="text-sm font-bold text-foreground">
                            {group.header ? t('grocery.whichKind', { header: group.header })
                                : group.label}
                        </p>
                        <div className="flex flex-wrap gap-2">
                            {((sheet.my_options || {})[group.id] || []).map((v) => (
                                <button key={`mine-${v}`} onClick={() => choose(v)}
                                    data-testid={`grocery-option-mine-${v}`}
                                    className="min-h-[52px] rounded-full border-2 border-emerald-500 px-5 text-base font-semibold text-emerald-700 dark:text-emerald-300">
                                    {v}
                                    <span className="ml-1.5 text-[11px] font-bold uppercase">{t('grocery.mine')}</span>
                                </button>
                            ))}
                            {(group.values || []).map((v) => (
                                <button key={v} onClick={() => choose(v)}
                                    data-testid={`grocery-option-${group.id}-${v}`}
                                    className="min-h-[52px] rounded-full border border-slate-300 px-5 text-base font-semibold text-foreground transition-colors hover:border-emerald-400 hover:text-emerald-600 dark:border-slate-600">
                                    {v}
                                </button>
                            ))}
                        </div>
                        <TypeItRow label={t('grocery.notListedOption')}
                            placeholder={t('grocery.notListedOptionHint')}
                            value={optionText} onChange={setOptionText} busy={busy}
                            testId="grocery-option-type"
                            onSubmit={async (text) => {
                                setBusy(true);
                                try {
                                    const r = sheet.saved_id
                                        ? await api(`/saved-items/${sheet.saved_id}/custom-option`,
                                            { method: 'POST', session, lang, body: { text } })
                                        : await api(`/items/${sheet.catalog_id}/custom-option`,
                                            { method: 'POST', session, lang,
                                                body: { text, group_id: group.id } });
                                    setSheet({ ...sheet, my_options: r.my_options });
                                    setOptionText('');
                                    choose(text);
                                } catch (e) { toast.error(customMessage(t, e.message)); }
                                finally { setBusy(false); }
                            }} />
                        <button onClick={() => setStep(groups.length)} data-testid="grocery-option-skip"
                            data-launcher-avoid="lift"
                            className="min-h-[52px] w-full rounded-2xl bg-slate-100 px-4 text-base font-bold text-slate-700 dark:bg-slate-800 dark:text-slate-200">
                            {t('grocery.skipAnyIsFine')}
                        </button>
                        <p className="text-xs text-muted-foreground">{t('grocery.skipNote')}</p>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {/* WHAT KIND, WHICH BRAND — same card, above the number. */}
                        {po ? (
                            <ProductOptionsCard api={api} session={session} lang={lang}
                                catalogId={sheet.catalog_id} options={po} value={poVal}
                                setValue={setPoVal}
                                reload={async () => {
                                    try {
                                        const r = await api(`/options/${sheet.catalog_id}`,
                                            { session, lang });
                                        setPo(r && r.group ? r : null);
                                    } catch (_e) { /* ignore */ }
                                }} />
                        ) : null}
                        {/* Qty box and Add on the SAME ROW — one thumb movement. */}
                        <QtyRow value={qty} max={sheet.qty_max} busy={busy}
                            packages={sheet.counts_packages} onChange={setQty}
                            testId="grocery-item-qty"
                            onAdd={async (n) => {
                                const ok = sheet.saved_id
                                    ? await saveCustom(sheet.name, sheet.category, n,
                                        picked.filter(Boolean))
                                    : await addNow(sheet.catalog_id, picked.filter(Boolean), n,
                                        po ? poVal : undefined);
                                if (ok) setSheet(null);
                            }} />
                        {/* A TYPED FOOD CAN HAVE ITS OWN KINDS TOO — named here
                            the first time, a chip from then on. */}
                        {sheet.saved_id ? (
                            <TypeItRow label={t('grocery.notListedOption')}
                                placeholder={t('grocery.notListedOptionHint')}
                                value={optionText} onChange={setOptionText} busy={busy}
                                testId="grocery-saved-option-type"
                                onSubmit={async (text) => {
                                    setBusy(true);
                                    try {
                                        const r = await api(
                                            `/saved-items/${sheet.saved_id}/custom-option`,
                                            { method: 'POST', session, lang, body: { text } });
                                        setOptionText('');
                                        setSheet({ ...sheet, my_options: r.my_options,
                                            groups: [{ id: '_mine', label: t('grocery.whichKindOwn'),
                                                values: r.my_options._mine }] });
                                        setPicked([text]);
                                        setStep(1);
                                    } catch (e) { toast.error(customMessage(t, e.message)); }
                                    finally { setBusy(false); }
                                }} />
                        ) : null}
                        {groups.length && !po ? (
                            <button onClick={() => { setStep(0); setPicked([]); }}
                                data-testid="grocery-option-change"
                                className="min-h-[44px] w-full text-sm font-semibold text-emerald-600">
                                {t('grocery.changeChoice')}
                            </button>
                        ) : null}
                    </div>
                )}
            </Shell>
        );
    }

    // ── one scrolling aisle: section headers, items underneath ────────────
    if (aisle) {
        return (
            <Shell title={aisle.aisle.name} onBack={() => setAisle(null)} onClose={onClose} frost={frost}
                testId="grocery-aisle-view">
                {/* Their own items in this aisle, first — a typed food is not a
                    second-class food. Rename or delete right here. */}
                {(aisle.my_items || []).length ? (
                    <section data-testid="grocery-my-items" className="space-y-2">
                        <h3 className="border-b border-emerald-200 pb-1.5 text-sm font-bold uppercase tracking-wide text-emerald-700 dark:border-emerald-500/30 dark:text-emerald-300">
                            {t('grocery.myItems')}
                        </h3>
                        {aisle.my_items.map((m) => (
                            <div key={m.saved_id} className="flex items-center gap-2"
                                data-testid={`grocery-my-item-${m.saved_id}`}>
                                {myEdit?.id === m.saved_id ? (
                                    <>
                                        <Input value={myEdit.text} maxLength={60}
                                            onChange={(e) => setMyEdit({ ...myEdit, text: e.target.value })}
                                            data-testid={`grocery-my-item-input-${m.saved_id}`}
                                            className="min-h-[48px] flex-1 text-base" />
                                        <Button onClick={() => renameSaved(m.saved_id, myEdit.text)}
                                            data-testid={`grocery-my-item-save-${m.saved_id}`}
                                            className="min-h-[48px] bg-emerald-600 text-white">
                                            <Check className="h-5 w-5" />
                                        </Button>
                                    </>
                                ) : (
                                    <>
                                        <button onClick={() => tapSaved(m.saved_id)}
                                            disabled={busy}
                                            data-testid={`grocery-my-item-add-${m.saved_id}`}
                                            className="flex min-h-[56px] flex-1 items-center gap-3 rounded-2xl border border-emerald-300 px-3 text-left dark:border-emerald-500/40">
                                            <span className="min-w-0 flex-1 truncate text-sm font-semibold text-foreground">{m.name}</span>
                                            <Plus className="h-5 w-5 shrink-0 text-emerald-600" />
                                        </button>
                                        <button onClick={() => setMyEdit({ id: m.saved_id, text: m.name })}
                                            aria-label={t('grocery.rename')}
                                            data-testid={`grocery-my-item-rename-${m.saved_id}`}
                                            className="flex min-h-[48px] min-w-[48px] items-center justify-center rounded-full text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800">
                                            <Pencil className="h-4 w-4" />
                                        </button>
                                        <button onClick={() => deleteSaved(m.saved_id)}
                                            aria-label={t('grocery.delete')}
                                            data-testid={`grocery-my-item-delete-${m.saved_id}`}
                                            className="flex min-h-[48px] min-w-[48px] items-center justify-center rounded-full text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800">
                                            <Trash2 className="h-4 w-4" />
                                        </button>
                                    </>
                                )}
                            </div>
                        ))}
                    </section>
                ) : null}
                {/* NO DEAD END INSIDE AN AISLE: the aisle is known, so nothing
                    is asked — it is saved and added in one tap. */}
                <TypeItRow label={t('grocery.notListed')} placeholder={t('grocery.notListedHint')}
                    value={typed} onChange={setTyped} busy={busy}
                    testId="grocery-aisle-type"
                    onSubmit={(text) => saveCustom(text, aisle.aisle.id)} />
                {aisle.sections.map((s) => (
                    <section key={s.key} data-testid={`grocery-section-${s.key}`} className="space-y-2">
                        <h3 className="flex items-center gap-2 border-b border-slate-200 pb-1.5 text-sm font-bold uppercase tracking-wide text-slate-600 dark:border-slate-800 dark:text-slate-300">
                            <GroceryIcon icon={s.icon} size={22} alt="" />{s.name}
                        </h3>
                        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                            {s.items.map((i) => (
                                <button key={i.catalog_id} disabled={busy}
                                    onClick={() => tapItem(i.catalog_id)}
                                    data-testid={`grocery-aisle-item-${i.catalog_id}`}
                                    className="flex min-h-[60px] items-center gap-3 rounded-2xl border border-slate-200 px-3 py-2 text-left transition-colors hover:border-emerald-400 dark:border-slate-800">
                                    <span className={tapped === i.catalog_id ? 'play-icon-tap' : ''}>
                                        <GroceryIcon icon={i.icon} badge={i.badge} size={34} alt="" />
                                    </span>
                                    <span className="min-w-0 flex-1">
                                        <span className="block truncate text-sm font-semibold text-foreground">{i.name}</span>
                                        {i.sold_by !== 'each' ? (
                                            <span className="text-[11px] font-semibold text-sky-700 dark:text-sky-300">{t('grocery.byWeight')}</span>
                                        ) : null}
                                    </span>
                                    <Plus className="h-5 w-5 shrink-0 text-emerald-600" />
                                </button>
                            ))}
                        </div>
                    </section>
                ))}
            </Shell>
        );
    }

    // ── the aisle tiles + a search box at the top ─────────────────────────
    return (
        <Shell title={t('grocery.addToList')} onClose={onClose} testId="grocery-add-flow" frost={frost}>
            <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input value={q} onChange={(e) => search(e.target.value)}
                    placeholder={t('grocery.searchPlaceholder')} data-testid="grocery-add-search"
                    className="min-h-[52px] pl-9 text-base" />
            </div>
            {q.trim().length >= 2 ? (
                <div className="divide-y overflow-hidden rounded-2xl border border-slate-200 dark:divide-slate-800 dark:border-slate-800"
                    data-testid="grocery-add-search-results">
                    {results.map((r) => (
                        <button key={r.catalog_id || `saved-${r.saved_id}`}
                            onClick={() => (r.catalog_id ? tapItem(r.catalog_id)
                                : tapSaved(r.saved_id))} disabled={busy}
                            data-testid={r.catalog_id ? `grocery-add-result-${r.catalog_id}`
                                : `grocery-add-result-saved-${r.saved_id}`}
                            className="flex min-h-[56px] w-full items-center gap-3 px-3 py-2 text-left hover:bg-slate-50 dark:hover:bg-slate-900">
                            <GroceryIcon icon={r.icon} badge={r.badge} size={30} alt="" />
                            <span className="min-w-0 flex-1">
                                <span className="block truncate text-sm font-semibold text-foreground">{r.name}</span>
                                {r.mine ? (
                                    <span className="text-[11px] font-bold uppercase text-emerald-600 dark:text-emerald-300">{t('grocery.mine')}</span>
                                ) : r.size ? (
                                    <span className="text-[11px] text-muted-foreground">{r.size}</span>
                                ) : null}
                            </span>
                            <Plus className="h-5 w-5 text-emerald-600" />
                        </button>
                    ))}
                    {results.length === 0 ? (
                        <p className="px-3 py-4 text-sm text-muted-foreground" data-testid="grocery-no-matches">
                            {t('grocery.noMatches')}
                        </p>
                    ) : null}
                </div>
            ) : null}
            {/* NO DEAD END: whatever they typed can go on the list in their own
                words, and is remembered for this household from now on. */}
            {q.trim().length >= 2 ? (
                <TypeItRow label={t('grocery.notListed')} placeholder={t('grocery.notListedHint')}
                    value={typed || q.trim()} onChange={setTyped} busy={busy}
                    testId="grocery-search-type" onSubmit={(text) => askForAisle(text)} />
            ) : null}
            {aisles === null ? (
                <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-emerald-500" /></div>
            ) : (
                <div className="grid grid-cols-3 gap-3 sm:grid-cols-4">
                    {aisles.map((a) => (
                        <button key={a.id} onClick={() => openAisle(a.id)} disabled={busy}
                            data-testid={`grocery-aisle-tile-${a.id}`}
                            className="flex min-h-[96px] flex-col items-center justify-center gap-1.5 rounded-2xl border border-slate-200 p-2 text-center transition-colors hover:border-emerald-400 dark:border-slate-800">
                            <GroceryIcon icon={a.icon} size={38} alt="" />
                            <span className="text-xs font-semibold leading-tight text-foreground">{a.name}</span>
                        </button>
                    ))}
                </div>
            )}
        </Shell>
    );
}
